import actions from "./actions";
import { termInChinese } from "@/utility/termInChinese.js";

export default {
  actions,
  state: {
    errorMessage: "",
    chartProfitYear: {},
    chartProfitQuarter: {}
  },
  mutations: {
    fetchErrorMessage(state, payload) {
      state.errorMessage = payload || "";
    },
    chartProfitYear(state, payload) {
      state.chartProfitYear = payload;
    },
    chartProfitQuarter(state, payload) {
      state.chartProfitQuarter = payload;
    }
  },
  getters: {
    // 輸入無效代號或 call API 伺服器失效，接收錯誤提示訊息。
    errorMessage: state => state.errorMessage,
    chartColors: () => {
      return {
        eucalyptus: "#28a745", // 桉樹色
        dodgerBlue: "#007bff", // 道奇色
        fuchsiaBlue: "#6f42c1", // 紫紅色
        amber: "#ffc107", // 琥珀色
        amaranth: "#dc3545", // 莧菜色
        pumpkin: "#fd7e14", // 南瓜色
        pelorous: "#17a2b8", // 藍綠色
        cerise: "#e83e8c", // 櫻桃色,
        mountainMeadow: "#20c997" // 山草甸
      };
    },
    chartProfitYear: ({ chartProfitYear }) => chartProfitYear,
    chartProfitQuarter: ({ chartProfitQuarter }) => chartProfitQuarter,
    // ------------------ 折線圖複用格式轉換區 ------------------
    formatToChartMap: () => (data, axesYUnit) => {
      //data 是 api 原始資料，用它的第一筆取 keys，且不要 year key
      const keys = Object.keys(data.slice().shift()).filter(
        field => field !== "year" || field !== "quarter"
      );

      const chartMap = keys.reduce((mResult, key) => {
        const xyData = data.map(item => {
          let formateValue;
          switch (axesYUnit) {
            //先轉成百分比，再取小數點第一位，以下捨去
            case "percentage":
              formateValue =
                item[key] === null ? NaN : Math.floor(item[key] * 1000) / 10;
              break;
            case "price":
              formateValue = item[key] === null ? NaN : item[key];
              break;
            //取小數點第一位，以下捨去
            case "number":
              formateValue =
                item[key] === null ? NaN : Math.floor(item[key] * 10) / 10;
              break;
            default:
              console.log("沒有傳入 chart 單位, 預設單位: price");
              formateValue = item[key] === null ? NaN : item[key];
              break;
          }
          return {
            x: item.year || item.quarter,
            //要回傳數字，chart 才會自動判斷 y 軸每個數字的間距
            y: Number(formateValue)
          };
        });
        mResult.set(key, {
          label: termInChinese.get(key),
          data: xyData
        });
        return mResult;
      }, new Map());
      return chartMap;
    },
    chartOptionsBase() {
      return {
        //canvas 不縮放。若縮放，再手機版時高的比例被拉長
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            /* eslint-disable-next-line no-unused-vars */
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              // 修改每點 hover 出的數字，加入千本位逗號
              const yValue = tooltipItem.yLabel
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return `${label}: ${yValue}`;
            }
          }
        }
      };
    },
    formatToChartOptions: () => (xAxesLabels, chartOptions) => {
      return {
        title: {
          text: chartOptions.title
        },
        scales: {
          yAxes: [
            {
              ticks: {
                // step 用來設定間距，null 表示自動計算
                stepSize: chartOptions.step,
                callback(value) {
                  if (value !== 0) {
                    // 修改 Y 軸標題格式
                    switch (chartOptions.axesYUnit) {
                      //百分比，y 軸顯示數字加上「%」
                      case "percentage":
                        return `${value} %`;
                      //元，y 軸顯示數字加上「千本位逗號」跟「百萬」
                      case "price":
                        return `${value
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} 百萬`;
                      //數字，y 軸顯示數字加上「千本位逗號」
                      case "number":
                        return `${value
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
                      default:
                        console.log("沒有傳入 chart 單位, 預設單位: number");
                        return `${value
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
                    }
                  } else return 0;
                }
              }
            }
          ],
          xAxes: [
            {
              labels: xAxesLabels
            }
          ]
        }
      };
    },
    formatToChartDataYear: (
      state,
      { formatToChartMap, chartOptionsBase, formatToChartOptions }
    ) => (data, chartFields, chartOptions) => {
      //year 排序大到小，ex 2020 > 2012
      const reverseData = data.slice().sort((a, b) => b.year - a.year);
      const mapData = formatToChartMap(reverseData, chartOptions.axesYUnit);
      const datasets = chartFields.map(field => {
        return {
          ...mapData.get(field.key),
          pointBackgroundColor: field.color,
          borderColor: field.color
        };
      });

      // X 軸取末兩碼，ex: X 軸呈現 2019，取 2019 末兩碼
      const fieldKey = chartFields[0].key;
      const xAxesLabels = mapData
        .get(fieldKey)
        .data.map(item => item.x.toString().slice(-2));

      const chartData = {
        data: {
          datasets
        },
        options: {
          ...chartOptionsBase,
          ...formatToChartOptions(xAxesLabels, chartOptions)
        }
      };
      console.log(chartData);
      return chartData;
    },
    formatToChartDataQuarter: (
      state,
      { formatToChartMap, chartOptionsBase, formatToChartOptions }
    ) => (data, chartFields, chartOptions) => {
      //quarter 排序大到小，ex 2020.4Q > 2019.4Q
      const reverseData = [...data].reverse();
      const mapData = formatToChartMap(reverseData, chartOptions.axesYUnit);
      const datasets = chartFields.map(field => {
        return {
          ...mapData.get(field.key),
          pointBackgroundColor: field.color,
          borderColor: field.color
        };
      });

      // X 軸取末五碼，ex: X 軸呈現 19.4Q，取 2019.4Q 末五碼
      const fieldKey = chartFields[0].key;
      const xAxesLabels = mapData
        .get(fieldKey)
        .data.map(item => item.x.toString().slice(-5));

      const chartData = {
        data: {
          datasets
        },
        options: {
          ...chartOptionsBase,
          ...formatToChartOptions(xAxesLabels, chartOptions)
        }
      };
      return chartData;
    }
  }
};
